// Modals for Vocab editing flows:
//   - Phase 3: Create Scheme + Create Concept
//   - Phase 4.x: Move Concept (set new skos:broader or skos:topConceptOf)
//
// All share the ui/Modal shell and rely on the parent page
// to invalidate the tree query + select the affected URI on success.

import { useEffect, useMemo, useRef, useState } from "react";
import { invalidateAfterEdit } from '@/lib/invalidate';
import { useAuth } from "react-oidc-context";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchVdbTopClasses } from "@/lib/useVdb";
import { BASE_CLASS_NAMES } from "@/lib/baseClassColors";
import { isQros, useMe } from "@/lib/useMe";
import { cn } from "@/lib/utils";
import {
  createConcept,
  createScheme,
  filterTree,
  findParentInTree,
  patchConcept,
  patchScheme,
  type ConceptNode,
  type CreateConceptRequest,
  type CreateResponse,
  type SchemeNode,
  type VocabTreeResponse,
} from "@/lib/useVocab";
import { buttonVariants } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

/**
 * What the Move modal hands back so the page can offer Undo. `null`
 * means the concept had no detectable parent in the tree before the
 * move (orphan or off-tree), in which case Undo would require backend
 * support for clearing both broader + topConceptOf and we don't offer
 * it.
 */
export type MovePreviousParent = {
  kind: "broader" | "scheme";
  uri: string;
  label: string;
} | null;

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "error"; message: string };

// ---- Create Scheme ----

interface NewSchemeModalProps {
  open: boolean;
  graph: string;
  onClose: () => void;
  onCreated: (created: CreateResponse) => void;
}

export function NewSchemeModal({
  open,
  graph,
  onClose,
  onCreated,
}: NewSchemeModalProps) {
  const auth = useAuth();
  const me = useMe();
  const qros = isQros(me.data);
  const queryClient = useQueryClient();
  const labelRef = useRef<HTMLInputElement | null>(null);
  const [prefLabel, setPrefLabel] = useState("");
  const [description, setDescription] = useState("");
  const [base, setBase] = useState(":Vocabulary");
  const [state, setState] = useState<SubmitState>({ status: "idle" });

  // The qros base classes a new vocabulary class can subclass (rule 5). The base
  // classes are written in the native blank-prefix form (`:Name`); identify them
  // by local name rather than by a literal `qros:` prefix.
  const token = auth.user?.id_token ?? null;
  const basesQuery = useQuery({
    queryKey: ["vdb", "top-classes", "all"],
    queryFn: () => fetchVdbTopClasses(token as string, null),
    enabled: open && !!token,
  });
  // All existing classes are valid anchors (user 2026-07-03) — the 11 fixed
  // bases plus every class in the store, even descendants of the bases. The
  // bases are guaranteed entries (scaffolding may carry no instances, so the
  // VDB list alone can miss them); fetched classes follow alphabetically.
  const fetched = (basesQuery.data ?? []).filter(
    (c) => !BASE_CLASS_NAMES.includes(c.uri.replace(/^(qros)?:/, "")),
  );
  const bases = [
    ...BASE_CLASS_NAMES.map((n) => ({ uri: `:${n}`, label: n })),
    ...fetched
      .map((c) => ({ uri: c.uri, label: c.label ?? c.uri }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  useEffect(() => {
    if (!open) return;
    setPrefLabel("");
    setDescription("");
    setBase(":Vocabulary");
    setState({ status: "idle" });
    const id = window.requestAnimationFrame(() => labelRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && state.status !== "submitting") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, state.status, onClose]);

  if (!open) return null;

  const canSubmit =
    prefLabel.trim().length > 0 &&
    state.status !== "submitting" &&
    !!auth.user?.id_token;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const token = auth.user?.id_token;
    if (!token) return;
    setState({ status: "submitting" });
    const request = {
      graph,
      pref_label: prefLabel.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(qros && base ? { base } : {}),
    };
    const result = await createScheme(request, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, null);
      onCreated(result.data);
    } else {
      setState({
        status: "error",
        message: result.message,
      });
    }
  }

  return (
    <Modal
      title={qros ? "New vocabulary class" : "New concept scheme"}
      onClose={onClose}
      busy={state.status === "submitting"}
    >
      <form onSubmit={handleSubmit} className="px-5 py-4">
        <Field label="Preferred label" required>
          <input
            ref={labelRef}
            type="text"
            value={prefLabel}
            onChange={(e) => setPrefLabel(e.target.value)}
            disabled={state.status === "submitting"}
            maxLength={200}
            className={inputClass}
            placeholder="e.g. Genre"
          />
        </Field>
        {qros && (
          <Field
            label="Base class"
            hint="The class this vocabulary descends from — any existing class, base or descendant"
          >
            <select
              value={base}
              onChange={(e) => setBase(e.target.value)}
              disabled={state.status === "submitting"}
              className={inputClass}
            >
              {bases.map((cls) => (
                <option key={cls.uri} value={cls.uri}>
                  {cls.label || cls.uri}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Description" hint="Optional">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={state.status === "submitting"}
            rows={3}
            maxLength={1000}
            className={inputClass}
            placeholder="Short note on what this vocabulary is for."
          />
        </Field>
        {state.status === "error" && <ErrorBanner message={state.message} />}
        <ModalFooter
          submitting={state.status === "submitting"}
          canSubmit={canSubmit}
          onCancel={onClose}
          submitLabel="Create"
        />
      </form>
    </Modal>
  );
}

// ---- Create Concept ----

interface NewConceptModalProps {
  open: boolean;
  graph: string;
  /** Parent context — drives whether the new concept is a top concept or narrower. */
  parent: ParentContext | null;
  onClose: () => void;
  onCreated: (created: CreateResponse) => void;
}

export interface ParentContext {
  /** "scheme" creates a top concept; "concept" creates a narrower term. */
  kind: "scheme" | "concept";
  uri: string;
  /** Display label used in the modal header to confirm what we're attaching to. */
  label: string;
}

export function NewConceptModal({
  open,
  graph,
  parent,
  onClose,
  onCreated,
}: NewConceptModalProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const labelRef = useRef<HTMLInputElement | null>(null);
  const [prefLabel, setPrefLabel] = useState("");
  const [altLabelsText, setAltLabelsText] = useState("");
  const [definition, setDefinition] = useState("");
  const [scopeNote, setScopeNote] = useState("");
  const [additionalClass, setAdditionalClass] = useState("");
  const [state, setState] = useState<SubmitState>({ status: "idle" });

  // Known classes for the optional additional-class selector (select-only).
  const token = auth.user?.id_token ?? null;
  const classesQuery = useQuery({
    // All graphs (null): class definitions live in the ontology graph, not the
    // vocabulary graph being edited.
    queryKey: ["vdb", "top-classes", "all"],
    queryFn: () => fetchVdbTopClasses(token as string, null),
    enabled: open && !!token,
  });

  useEffect(() => {
    if (!open) return;
    setPrefLabel("");
    setAltLabelsText("");
    setDefinition("");
    setScopeNote("");
    setAdditionalClass("");
    setState({ status: "idle" });
    const id = window.requestAnimationFrame(() => labelRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && state.status !== "submitting") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, state.status, onClose]);

  if (!open || !parent) return null;

  const canSubmit =
    prefLabel.trim().length > 0 &&
    state.status !== "submitting" &&
    !!auth.user?.id_token;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const token = auth.user?.id_token;
    if (!token || !parent) return;
    setState({ status: "submitting" });
    const altLabels = altLabelsText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const request: CreateConceptRequest = {
      graph,
      pref_label: prefLabel.trim(),
      ...(parent.kind === "scheme"
        ? { scheme: parent.uri }
        : { broader: parent.uri }),
      ...(altLabels.length > 0 ? { alt_labels: altLabels } : {}),
      ...(definition.trim() ? { definition: definition.trim() } : {}),
      ...(scopeNote.trim() ? { scope_note: scopeNote.trim() } : {}),
      ...(additionalClass ? { additional_class: additionalClass } : {}),
    };
    const result = await createConcept(request, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, null);
      onCreated(result.data);
    } else {
      setState({
        status: "error",
        message: result.message,
      });
    }
  }

  const headerSuffix =
    parent.kind === "scheme"
      ? `top concept of ${parent.label}`
      : `narrower than ${parent.label}`;

  return (
    <Modal
      title={`New concept — ${headerSuffix}`}
      onClose={onClose}
      busy={state.status === "submitting"}
    >
      <form onSubmit={handleSubmit} className="px-5 py-4">
        <Field label="Preferred label" required>
          <input
            ref={labelRef}
            type="text"
            value={prefLabel}
            onChange={(e) => setPrefLabel(e.target.value)}
            disabled={state.status === "submitting"}
            maxLength={200}
            className={inputClass}
            placeholder="e.g. Healing Factor"
          />
        </Field>
        <Field
          label="Alternative labels"
          hint="Comma- or newline-separated. Optional."
        >
          <input
            type="text"
            value={altLabelsText}
            onChange={(e) => setAltLabelsText(e.target.value)}
            disabled={state.status === "submitting"}
            maxLength={500}
            className={inputClass}
            placeholder="e.g. Regeneration, Self-healing"
          />
        </Field>
        <Field
          label="Additional class"
          hint="Optional — also types the concept as this class"
        >
          <select
            value={additionalClass}
            onChange={(e) => setAdditionalClass(e.target.value)}
            disabled={state.status === "submitting"}
            className={inputClass}
          >
            <option value="">(none)</option>
            {(classesQuery.data ?? []).map((cls) => (
              <option key={cls.uri} value={cls.uri}>
                {cls.label || cls.uri}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Definition" hint="Optional">
          <textarea
            value={definition}
            onChange={(e) => setDefinition(e.target.value)}
            disabled={state.status === "submitting"}
            rows={2}
            maxLength={2000}
            className={inputClass}
            placeholder="What this concept means."
          />
        </Field>
        <Field
          label="Scope note"
          hint="Optional — usage guidance for taxonomists"
        >
          <textarea
            value={scopeNote}
            onChange={(e) => setScopeNote(e.target.value)}
            disabled={state.status === "submitting"}
            rows={2}
            maxLength={2000}
            className={inputClass}
            placeholder="e.g. Use only when…"
          />
        </Field>
        {state.status === "error" && <ErrorBanner message={state.message} />}
        <ModalFooter
          submitting={state.status === "submitting"}
          canSubmit={canSubmit}
          onCancel={onClose}
          submitLabel="Create concept"
        />
      </form>
    </Modal>
  );
}

// ---- Shared bits ----

const inputClass =
  "mt-1 w-full rounded-md border border-border px-3 py-2 text-sm text-tres-text-primary placeholder:text-tres-text-secondary focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action disabled:bg-muted";


// ---- Edit Scheme (desktop parity: title + description, single-step) ----

interface EditSchemeModalProps {
  scheme: SchemeNode | null;
  graph: string;
  onClose: () => void;
  onSaved: () => void;
}

export function EditSchemeModal({
  scheme,
  graph,
  onClose,
  onSaved,
}: EditSchemeModalProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [state, setState] = useState<SubmitState>({ status: "idle" });

  useEffect(() => {
    if (!scheme) return;
    setTitle(scheme.pref_label);
    setDescription(scheme.description ?? "");
    setState({ status: "idle" });
  }, [scheme]);

  if (!scheme) return null;

  const canSubmit = title.trim().length > 0 && state.status !== "submitting";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const token = auth.user?.id_token;
    if (!token || !scheme) return;
    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();
    const body: {
      uri: string;
      graph: string;
      title?: string;
      description?: string;
    } = {
      uri: scheme.uri,
      graph,
    };
    if (trimmedTitle !== scheme.pref_label) body.title = trimmedTitle;
    if (trimmedDesc !== (scheme.description ?? ""))
      body.description = trimmedDesc;
    if (body.title === undefined && body.description === undefined) {
      onClose();
      return;
    }
    setState({ status: "submitting" });
    const result = await patchScheme(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, null);
      onSaved();
    } else {
      setState({
        status: "error",
        message: result.message,
      });
    }
  }

  return (
    <Modal
      title="Edit vocabulary class"
      onClose={onClose}
      busy={state.status === "submitting"}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <p className="break-all text-xs text-tres-text-secondary">
          {scheme.uri}
        </p>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className="w-full rounded border border-border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-tres-action/40"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={1000}
            className="w-full rounded border border-border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-tres-action/40"
            placeholder="Leave empty to clear the description."
          />
        </label>
        {state.status === "error" && (
          <p className="text-sm text-tres-error">{state.message}</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={state.status === "submitting"}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              "rounded bg-tres-action px-3 py-1 text-sm text-white",
              !canSubmit && "opacity-50",
            )}
          >
            {state.status === "submitting" ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---- Delete Scheme (desktop parity: 2-step confirm, explicit cascade) ----

interface DeleteSchemeModalProps {
  scheme: SchemeNode | null;
  graph: string;
  onClose: () => void;
  /** The choice, handed to the page: the modal closes at once and the page
   *  runs the delete where the user can see it (Ch08 §2.5). */
  onConfirm: (choice: SchemeDeleteChoice) => void;
}

export interface SchemeDeleteChoice {
  mode: 'delete' | 'reassign';
  reassignTo?: string;
  members: number;
}

/** Count all concepts under a scheme/class (top concepts + their descendants). */
function countMembers(scheme: SchemeNode): number {
  const walk = (nodes: SchemeNode["top_concepts"]): number =>
    nodes.reduce((n, c) => n + 1 + walk(c.children), 0);
  return walk(scheme.top_concepts);
}

export function DeleteSchemeModal({
  scheme,
  onClose,
  onConfirm,
}: DeleteSchemeModalProps) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? null;
  const [state, setState] = useState<SubmitState>({ status: "idle" });
  // What happens to the member concepts: delete them, or move them to another class.
  const [mode, setMode] = useState<"delete" | "reassign">("delete");
  const [reassignTo, setReassignTo] = useState("");

  // Candidate classes to reassign into (any known class except this one).
  const classesQuery = useQuery({
    queryKey: ["vdb", "top-classes", "all"],
    queryFn: () => fetchVdbTopClasses(token as string, null),
    enabled: !!scheme && !!token,
  });

  useEffect(() => {
    if (scheme) {
      setState({ status: "idle" });
      setMode("delete");
      setReassignTo("");
    }
  }, [scheme]);

  if (!scheme) return null;

  const members = countMembers(scheme);

  function handleDelete() {
    if (!scheme) return;
    if (mode === "reassign" && !reassignTo) {
      setState({ status: "error", message: "Choose a class to move concepts to." });
      return;
    }
    onConfirm({ mode, ...(mode === "reassign" ? { reassignTo } : {}), members });
  }
  const submitting = state.status === "submitting";
  const options = (classesQuery.data ?? []).filter((c) => c.uri !== scheme.uri);

  return (
    <Modal
      title="Delete vocabulary class"
      onClose={onClose}
      busy={submitting}
    >
      <div className="space-y-3 text-sm">
        <p>
          Delete <span className="font-semibold">{scheme.pref_label}</span>?
        </p>
        <p className="break-all text-xs text-tres-text-secondary">
          {scheme.uri}
        </p>
        {members > 0 ? (
          <>
            <p>
              This class has{" "}
              <span className="font-semibold">
                {members} concept{members === 1 ? "" : "s"}
              </span>
              . Choose what happens to them:
            </p>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="delete-mode"
                checked={mode === "delete"}
                onChange={() => setMode("delete")}
                disabled={submitting}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-tres-error">
                  Delete the {members} concept{members === 1 ? "" : "s"}
                </span>{" "}
                along with the class. This cannot be undone.
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="delete-mode"
                checked={mode === "reassign"}
                onChange={() => setMode("reassign")}
                disabled={submitting}
                className="mt-0.5"
              />
              <span className="flex-1">
                <span className="font-medium">Move the concepts</span> to another
                class, then delete this class.
                {mode === "reassign" && (
                  <select
                    value={reassignTo}
                    onChange={(e) => setReassignTo(e.target.value)}
                    disabled={submitting}
                    className="mt-1 w-full rounded border border-border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-tres-action/40"
                  >
                    <option value="">Choose a class…</option>
                    {options.map((c) => (
                      <option key={c.uri} value={c.uri}>
                        {c.label || c.uri}
                      </option>
                    ))}
                  </select>
                )}
              </span>
            </label>
          </>
        ) : (
          <p className="font-medium text-tres-error">
            This class has no concepts. Deleting it cannot be undone.
          </p>
        )}
        {state.status === "error" && (
          <p className="text-tres-error">{state.message}</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={mode === "reassign" && !reassignTo}
            className={buttonVariants({ variant: 'dangerFilled', size: 'md' })}
          >
            {submitting
              ? "Working…"
              : mode === "reassign"
                ? "Move concepts & delete class"
                : members > 0
                  ? "Delete class & all concepts"
                  : "Delete class"}
          </button>
        </div>
      </div>
    </Modal>
  );
}


interface FieldProps {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, hint, required, children }: FieldProps) {
  return (
    <label className="mb-3 block">
      <span className="text-sm font-medium text-tres-text-primary">
        {label}
        {required && <span className="ml-1 text-tres-error">*</span>}
        {hint && (
          <span className="ml-2 text-xs font-normal text-tres-text-secondary">
            {hint}
          </span>
        )}
      </span>
      {children}
    </label>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <p className="mt-3 rounded-md bg-tres-error/5 px-3 py-2 text-xs text-tres-error">
      {message}
    </p>
  );
}

interface ModalFooterProps {
  submitting: boolean;
  canSubmit: boolean;
  onCancel: () => void;
  submitLabel: string;
}

function ModalFooter({
  submitting,
  canSubmit,
  onCancel,
  submitLabel,
}: ModalFooterProps) {
  return (
    <div className="mt-5 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        className={buttonVariants({ variant: 'secondary', size: 'lg' })}
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={!canSubmit}
        className={cn(
          "rounded-md bg-tres-action px-4 py-2 text-sm font-medium text-white",
          "hover:opacity-90 disabled:opacity-50",
        )}
      >
        {submitting ? "Creating…" : submitLabel}
      </button>
    </div>
  );
}

// ---- Phase 4.x: Move concept ----

interface MoveTarget {
  kind: "broader" | "scheme";
  uri: string;
  label: string;
}

interface MoveConceptModalProps {
  open: boolean;
  graph: string;
  /** The concept being moved. */
  conceptUri: string;
  conceptLabel: string;
  /** The full tree, used as the picker source. */
  tree: VocabTreeResponse | null | undefined;
  onClose: () => void;
  /**
   * Called after a successful move. `previousParent` carries the
   * concept's pre-move parent so the page can offer an Undo banner;
   * `null` when no parent was detectable (rare).
   */
  onMoved: (previousParent: MovePreviousParent) => void;
}

/**
 * Picker modal for setting a concept's new structural parent. Click
 * any scheme in the tree to make this concept a top concept of that
 * scheme; click any other concept to make this concept a child of it.
 * The concept being moved and its own descendants are excluded from
 * the picker — same cycle protection the backend enforces, mirrored
 * client-side so the user never even sees an invalid target.
 */
export function MoveConceptModal({
  open,
  graph,
  conceptUri,
  conceptLabel,
  tree,
  onClose,
  onMoved,
}: MoveConceptModalProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<MoveTarget | null>(null);
  const [search, setSearch] = useState("");
  const [state, setState] = useState<SubmitState>({ status: "idle" });

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setSearch("");
    setState({ status: "idle" });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && state.status !== "submitting") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, state.status, onClose]);

  // Pre-compute the set of URIs to exclude: the concept itself plus
  // every descendant (preventing cycles client-side as well as on
  // the server).
  const excluded = useMemo(() => {
    const set = new Set<string>([conceptUri]);
    if (!tree) return set;
    for (const scheme of tree.schemes) {
      for (const top of scheme.top_concepts) {
        collectDescendantsIfMatch(top, conceptUri, set, false);
      }
    }
    return set;
  }, [tree, conceptUri]);

  const visibleTree = useMemo(() => {
    if (!tree) return null;
    return filterTree(tree, search);
  }, [tree, search]);

  if (!open) return null;

  const canSubmit =
    target !== null && state.status !== "submitting" && !!auth.user?.id_token;

  async function handleSubmit() {
    const token = auth.user?.id_token;
    if (!token || !target) return;
    setState({ status: "submitting" });
    // Capture the concept's CURRENT parent before we mutate it, so the
    // page can render an Undo banner that knows where to put it back.
    // Read this from the tree snapshot we already have; no extra
    // round trip needed.
    const previousParent: MovePreviousParent = tree
      ? findParentInTree(tree, conceptUri)
      : null;
    const body = {
      uri: conceptUri,
      graph,
      ...(target.kind === "broader"
        ? { broader: target.uri }
        : { scheme: target.uri }),
    };
    const result = await patchConcept(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, null);
      onMoved(previousParent);
    } else {
      setState({
        status: "error",
        message: result.message,
      });
    }
  }

  return (
    <Modal
      title="Move concept"
      onClose={onClose}
      busy={state.status === "submitting"}
    >
      <div>
        <p className="text-sm text-tres-text-secondary">
          Move{" "}
          <span className="font-medium text-tres-text-primary">
            {conceptLabel}
          </span>{" "}
          under another concept or to a different scheme. Click a scheme to make
          this a top concept; click a concept to make this its child.
        </p>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by label…"
          disabled={state.status === "submitting"}
          className="mt-3 w-full rounded-md border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tres-action/40"
        />

        <div className="mt-2 max-h-80 overflow-y-auto rounded-md border border-border bg-muted p-2 text-sm">
          {!visibleTree && (
            <p className="px-2 py-1 text-tres-text-secondary">No matches.</p>
          )}
          {visibleTree?.schemes.map((s) => (
            <SchemePickerRow
              key={s.uri}
              scheme={s}
              excluded={excluded}
              target={target}
              onPick={(t) => setTarget(t)}
            />
          ))}
        </div>

        {target && (
          <p className="mt-3 text-xs text-tres-text-secondary">
            New parent:{" "}
            <span className="font-medium text-tres-text-primary">
              {target.label}
            </span>{" "}
            <span className="text-tres-action">
              ({target.kind === "scheme" ? "top concept of" : "child of"})
            </span>
          </p>
        )}

        {state.status === "error" && <ErrorBanner message={state.message} />}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={state.status === "submitting"}
            className={buttonVariants({ variant: 'secondary', size: 'lg' })}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className={cn(
              "rounded-md bg-tres-action px-4 py-2 text-sm font-medium text-white",
              "hover:opacity-90 disabled:opacity-50",
            )}
          >
            {state.status === "submitting" ? "Moving…" : "Move"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Collect a concept and all its descendants into `acc` if either the
 * concept itself or any descendant matches `matchUri`. Recursive
 * because the tree is recursive. The `foundMatch` parameter threads
 * the "have we seen the match in this branch" signal down so we add
 * everything below the match once we hit it.
 */
function collectDescendantsIfMatch(
  node: ConceptNode,
  matchUri: string,
  acc: Set<string>,
  foundMatch: boolean,
): boolean {
  const matchedHere = foundMatch || node.uri === matchUri;
  if (matchedHere) acc.add(node.uri);
  for (const child of node.children) {
    collectDescendantsIfMatch(child, matchUri, acc, matchedHere);
  }
  return matchedHere;
}

function SchemePickerRow({
  scheme,
  excluded,
  target,
  onPick,
}: {
  scheme: SchemeNode;
  excluded: Set<string>;
  target: MoveTarget | null;
  onPick: (t: MoveTarget) => void;
}) {
  const isSelected = target?.kind === "scheme" && target.uri === scheme.uri;
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() =>
          onPick({ kind: "scheme", uri: scheme.uri, label: scheme.pref_label })
        }
        className={cn(
          "block w-full rounded px-2 py-1 text-left",
          isSelected
            ? "bg-tres-action text-white"
            : "text-tres-text-primary hover:bg-card",
        )}
      >
        <span className="font-medium">{scheme.pref_label}</span>
        <span className="ml-2 text-[10px] uppercase tracking-wide text-tres-text-secondary">
          scheme
        </span>
      </button>
      <div className="ml-3">
        {scheme.top_concepts.map((c) => (
          <ConceptPickerRow
            key={c.uri}
            concept={c}
            depth={0}
            excluded={excluded}
            target={target}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  );
}

function ConceptPickerRow({
  concept,
  depth,
  excluded,
  target,
  onPick,
}: {
  concept: ConceptNode;
  depth: number;
  excluded: Set<string>;
  target: MoveTarget | null;
  onPick: (t: MoveTarget) => void;
}) {
  const isExcluded = excluded.has(concept.uri);
  const isSelected = target?.kind === "broader" && target.uri === concept.uri;
  return (
    <div>
      {isExcluded ? (
        <div
          className="block w-full px-2 py-1 text-left text-tres-text-secondary opacity-50"
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          title={
            concept.uri === target?.uri
              ? "This concept"
              : "Excluded: would create a cycle"
          }
        >
          {concept.pref_label}
        </div>
      ) : (
        <button
          type="button"
          onClick={() =>
            onPick({
              kind: "broader",
              uri: concept.uri,
              label: concept.pref_label,
            })
          }
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          className={cn(
            "block w-full rounded px-2 py-1 text-left",
            isSelected
              ? "bg-tres-action text-white"
              : "text-tres-text-primary hover:bg-card",
          )}
        >
          {concept.pref_label}
        </button>
      )}
      {concept.children.map((child) => (
        <ConceptPickerRow
          key={child.uri}
          concept={child}
          depth={depth + 1}
          excluded={excluded}
          target={target}
          onPick={onPick}
        />
      ))}
    </div>
  );
}
